/**
 * Sistema de referral simples e determinístico, sem necessidade de migration.
 *
 * O código é derivado dos primeiros 8 caracteres alfanuméricos do UUID do usuário,
 * em maiúsculas. Determinístico → o mesmo usuário sempre tem o mesmo código.
 *
 * O código é exposto publicamente como link `?ref=ABCD1234`. Ao chegar nessa URL,
 * `captureReferral()` armazena o código em localStorage. No signup, `consumeReferral()`
 * devolve o código para ser persistido em `profiles.referrer`.
 */

const STORAGE_KEY = 'ap_referral_code_v1'
const STORAGE_TTL_DAYS = 30

interface StoredReferral {
  code: string
  capturedAt: string
}

export const codeFromUserId = (userId: string | undefined | null): string => {
  if (!userId) return ''
  const cleaned = userId.replace(/[^a-zA-Z0-9]/g, '').toUpperCase()
  return cleaned.slice(0, 8)
}

export const buildReferralLink = (code: string, base?: string): string => {
  const origin =
    base || (typeof window !== 'undefined' ? window.location.origin : 'https://atividadepronta.com.br')
  return `${origin}/?ref=${encodeURIComponent(code)}`
}

export const captureReferral = (rawCode: string | null | undefined): void => {
  if (!rawCode || typeof window === 'undefined') return
  const code = rawCode.trim().toUpperCase().slice(0, 16)
  if (!code) return
  try {
    const payload: StoredReferral = { code, capturedAt: new Date().toISOString() }
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload))
  } catch {
    // ignora
  }
}

export const readReferral = (): string | null => {
  if (typeof window === 'undefined') return null
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as StoredReferral
    const ageDays = (Date.now() - new Date(parsed.capturedAt).getTime()) / (1000 * 60 * 60 * 24)
    if (ageDays > STORAGE_TTL_DAYS) {
      localStorage.removeItem(STORAGE_KEY)
      return null
    }
    return parsed.code
  } catch {
    return null
  }
}

export const consumeReferral = (): string | null => {
  const code = readReferral()
  if (code && typeof window !== 'undefined') {
    try {
      localStorage.removeItem(STORAGE_KEY)
    } catch {
      // ignora
    }
  }
  return code
}
